import React from 'react';

const GOLD = '#e53935';
const GOLD_DIM = '#c62828';
const STEEL = 'rgba(255,255,255,0.45)';

export type PlanTier = 'alpha' | 'shell' | 'kernel' | 'root';

export const getPlanTier = (planName: string): PlanTier => {
  const n = planName.toLowerCase();
  if (n.includes('root')) return 'root';
  if (n.includes('kernel')) return 'kernel';
  if (n.includes('alpha')) return 'alpha';
  return 'shell';
};

const TIER_META: Record<PlanTier, { level: string; label: string }> = {
  alpha: { level: '1', label: 'Starter' },
  shell: { level: '2', label: 'Entry' },
  kernel: { level: '3', label: 'Pro' },
  root: { level: '4', label: 'Elite' },
};

export const getTierMeta = (tier: PlanTier) => TIER_META[tier];

export const PLAN_TIERS: PlanTier[] = ['alpha', 'shell', 'kernel', 'root'];

export const getTierBarCount = (tier: PlanTier): number => PLAN_TIERS.indexOf(tier) + 1;

interface IconProps {
  className?: string;
}

export const ShellTierIcon: React.FC<IconProps> = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 32 32" fill="none" width="32" height="32" aria-hidden="true">
    <path d="M6 10h20v14H6z" stroke={STEEL} strokeWidth="1.25" strokeOpacity="0.5" />
    <path d="M10 14h12M10 18h12M10 22h8" stroke={GOLD_DIM} strokeWidth="1.25" strokeLinecap="round" />
    <rect x="8" y="8" width="16" height="4" rx="1" stroke={GOLD} strokeWidth="1.25" fill="rgba(229, 57, 53,0.08)" />
  </svg>
);

export const KernelTierIcon: React.FC<IconProps> = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 32 32" fill="none" width="32" height="32" aria-hidden="true">
    <rect x="8" y="8" width="16" height="16" rx="2" stroke={GOLD} strokeWidth="1.35" fill="rgba(229, 57, 53,0.1)" />
    <path d="M12 12h8v8h-8z" stroke={GOLD_DIM} strokeWidth="1" strokeOpacity="0.7" />
    <circle cx="16" cy="16" r="2" fill={GOLD}>
      <animate attributeName="opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite" />
    </circle>
    <path d="M16 4v3M16 25v3M4 16h3M25 16h3" stroke={GOLD_DIM} strokeWidth="1" strokeLinecap="round" strokeOpacity="0.6" />
  </svg>
);

export const RootTierIcon: React.FC<IconProps> = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 32 32" fill="none" width="32" height="32" aria-hidden="true">
    <path
      d="M16 5l2.2 5.5 5.8.5-4.3 3.8 1.3 5.7L16 17.5l-5 3-1.3-5.7-4.3-3.8 5.8-.5L16 5z"
      stroke={GOLD}
      strokeWidth="1.25"
      strokeLinejoin="round"
      fill="rgba(229, 57, 53,0.12)"
    />
    <circle cx="16" cy="16" r="6" stroke={GOLD_DIM} strokeWidth="1" strokeOpacity="0.5" />
    <path d="M16 13v6M13 16h6" stroke={GOLD} strokeWidth="1.25" strokeLinecap="round" />
  </svg>
);

const TIER_ICON_MAP: Record<PlanTier, React.FC<IconProps>> = {
  alpha: ShellTierIcon,
  shell: ShellTierIcon,
  kernel: KernelTierIcon,
  root: RootTierIcon,
};

export const PlanTierIcon: React.FC<{ tier: PlanTier; className?: string }> = ({ tier, className }) => {
  const Icon = TIER_ICON_MAP[tier];
  return <Icon className={className} />;
};

export const FeatureCheckIcon: React.FC<{
  included: boolean;
  isRestricted?: boolean;
  className?: string;
}> = ({ included, isRestricted = false, className = '' }) => {
  if (isRestricted) {
    return (
      <svg className={className} viewBox="0 0 16 16" fill="none" width="16" height="16" aria-hidden="true">
        <rect x="1" y="1" width="14" height="14" rx="2" stroke="rgba(248,113,113,0.4)" fill="rgba(248,113,113,0.08)" />
        <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="#fca5a5" strokeWidth="1.25" strokeLinecap="round" />
      </svg>
    );
  }
  if (included) {
    return (
      <svg className={className} viewBox="0 0 16 16" fill="none" width="16" height="16" aria-hidden="true">
        <rect x="1" y="1" width="14" height="14" rx="2" stroke="rgba(229, 57, 53,0.35)" fill="rgba(212,175,55,0.1)" />
        <path
          d="M4.5 8.2l2.2 2.2L11.5 5.5"
          stroke={GOLD}
          strokeWidth="1.35"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="pricing-feat-check-draw"
        />
      </svg>
    );
  }
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" width="16" height="16" aria-hidden="true">
      <rect x="1" y="1" width="14" height="14" rx="2" stroke="rgba(255,255,255,0.1)" fill="rgba(255,255,255,0.02)" />
      <path d="M5.5 8h5" stroke="#525252" strokeWidth="1.25" strokeLinecap="round" />
    </svg>
  );
};

export const BtcIcon: React.FC<IconProps> = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="none" aria-hidden="true">
    <circle cx="6" cy="6" r="5" stroke="#e53935" strokeWidth="0.75" fill="rgba(229, 57, 53,0.1)" />
    <path d="M6.8 4.2c0-.8-.6-1.2-1.6-1.2H4v4.8h1.4c1 0 1.6-.4 1.6-1.2 0-.5-.3-.9-.8-1 .6-.2 1-.7 1-1.4zm-1.2-.7h.6c.4 0 .6.2.6.5s-.2.5-.6.5h-.6V3.5zm.6 3.2H5.2c-.5 0-.8-.2-.8-.6s.3-.6.8-.6h1V6.7z" fill="#e53935" />
  </svg>
);

export const EthIcon: React.FC<IconProps> = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="none" aria-hidden="true">
    <path d="M6 1.5L6 6l4.5 2.5L6 1.5z" fill="rgba(229, 57, 53,0.35)" stroke="#e53935" strokeWidth="0.5" />
    <path d="M6 6v4.5l4.5-2L6 6z" fill="rgba(212,175,55,0.2)" stroke="#c62828" strokeWidth="0.5" />
    <path d="M6 1.5L1.5 4 6 6 10.5 4 6 1.5z" stroke="#e53935" strokeWidth="0.5" fill="none" />
  </svg>
);

export const SolIcon: React.FC<IconProps> = () => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="none" aria-hidden="true">
    <path
      d="M2.5 8.5l3.5-7 3.5 7H2.5z"
      stroke="#e53935"
      strokeWidth="0.75"
      fill="rgba(229, 57, 53,0.08)"
      strokeLinejoin="round"
    />
    <path d="M4 6.5h4" stroke="#c62828" strokeWidth="0.75" strokeLinecap="round" />
  </svg>
);
